'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Leader {
  id: number;
  name: string;
  position: string;
  bio: string;
  expertise: string[];
  image: string;
  alt: string;
  linkedin: string;
}

interface LeadershipSectionProps {
  className?: string;
}

const LeadershipSection = ({ className = '' }: LeadershipSectionProps) => {
  const [selectedLeader, setSelectedLeader] = useState<Leader | null>(null);

  const leaders: Leader[] = [
  {
    id: 1,
    name: 'James Mwangi',
    position: 'Chief Executive Officer',
    bio: 'With over 20 years of experience in African logistics and mining operations, James leads MylesDrive\'s strategic vision and regional expansion. His deep understanding of cross-border trade and regulatory frameworks has been instrumental in establishing our presence across six African nations.',
    expertise: ['Strategic Planning', 'Regional Expansion', 'Mining Logistics', 'Regulatory Compliance'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10d17e2d1-1763293910418.png",
    alt: 'Professional African businessman in navy suit with confident smile in modern office',
    linkedin: '#'
  },
  {
    id: 2,
    name: 'Sarah Ochieng',
    position: 'Chief Operations Officer',
    bio: 'Sarah brings 15 years of operational excellence in heavy equipment logistics and fleet management. Her innovative approach to route optimization and real-time tracking has revolutionized our service delivery and safety standards.',
    expertise: ['Operations Management', 'Fleet Optimization', 'Safety Protocols', 'Technology Integration'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c4625c15-1763299022465.png",
    alt: 'Professional African businesswoman in golden blazer with warm smile in corporate setting',
    linkedin: '#'
  },
  {
    id: 3,
    name: 'David Kamau',
    position: 'Chief Technology Officer',
    bio: 'David spearheads our digital transformation initiatives, implementing cutting-edge GPS tracking, telematics, and digital documentation systems. His vision has positioned MylesDrive as a technology leader in African logistics.',
    expertise: ['Digital Innovation', 'GPS Systems', 'Data Analytics', 'Platform Development'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_11ac42a5b-1763301932122.png",
    alt: 'Professional African tech executive in casual business attire with laptop in modern workspace',
    linkedin: '#'
  },
  {
    id: 4,
    name: 'Grace Mutua',
    position: 'Head of Safety & Compliance',
    bio: 'Grace ensures MylesDrive maintains the highest safety standards and regulatory compliance across all operations. Her leadership achieved our ISO 45001 certification and zero-incident milestone in 2021.',
    expertise: ['Safety Management', 'ISO Compliance', 'Risk Assessment', 'Training Programs'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17774ef61-1765069152947.png",
    alt: 'Professional African woman in safety vest with clipboard in industrial logistics facility',
    linkedin: '#'
  }];


  return (
    <section className={`py-16 lg:py-24 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
            <Icon name="UserGroupIcon" size={20} className="text-secondary" />
            <span className="font-body text-sm font-semibold text-secondary">Leadership Team</span>
          </div>
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
            Guided by Experience, Driven by Vision
          </h2>
          <p className="font-body text-lg text-muted-foreground">
            Our leadership team combines decades of African logistics expertise with a passion for innovation and operational excellence.
          </p>
        </div>

        {/* Leadership Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {leaders.map((leader) =>
          <div
            key={leader.id}
            className="bg-card rounded-xl overflow-hidden shadow-elevation-2 hover:shadow-elevation-4 transition-all duration-300 cursor-pointer group"
            onClick={() => setSelectedLeader(leader)}>

              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <AppImage
                src={leader.image}
                alt={leader.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="font-headline font-bold text-xl text-white mb-1">{leader.name}</h3>
                  <p className="font-body text-sm text-accent">{leader.position}</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex flex-wrap gap-2 mb-4">
                  {leader.expertise.slice(0, 2).map((skill, index) =>
                <span key={index} className="font-body text-xs bg-muted px-3 py-1 rounded-full text-muted-foreground">
                      {skill}
                    </span>
                )}
                </div>
                <button
                className="flex items-center space-x-2 text-secondary hover:text-accent transition-colors duration-300 font-body text-sm font-semibold"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedLeader(leader);
                }}>

                  <span>View Profile</span>
                  <Icon name="ArrowRightIcon" size={16} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        {selectedLeader &&
        <div
          className="fixed inset-0 bg-primary/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedLeader(null)}>

            <div
            className="bg-card rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-elevation-5"
            onClick={(e) => e.stopPropagation()}>

              {/* Modal Header */}
              <div className="relative h-64 lg:h-80">
                <AppImage
                src={selectedLeader.image}
                alt={selectedLeader.alt}
                className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 to-transparent" />
                <button
                onClick={() => setSelectedLeader(null)}
                className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm p-2 rounded-full hover:bg-white/30 transition-colors duration-300">

                  <Icon name="XMarkIcon" size={24} className="text-white" />
                </button>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="font-headline font-bold text-3xl text-white mb-2">{selectedLeader.name}</h3>
                  <p className="font-body text-lg text-accent">{selectedLeader.position}</p>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 lg:p-8">
                <p className="font-body text-base text-muted-foreground leading-relaxed mb-6">
                  {selectedLeader.bio}
                </p>

                <div className="mb-6">
                  <h4 className="font-headline font-bold text-lg text-foreground mb-3">Areas of Expertise</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedLeader.expertise.map((skill, index) =>
                  <span key={index} className="font-body text-sm bg-accent/10 text-secondary px-4 py-2 rounded-lg border border-accent/20">
                        {skill}
                      </span>
                  )}
                  </div>
                </div>

                <a
                href={selectedLeader.linkedin}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-accent to-secondary text-primary px-6 py-3 rounded-lg font-body font-semibold hover:shadow-golden-lg transition-all duration-300">

                  <Icon name="UserIcon" size={20} />
                  <span>Connect on LinkedIn</span>
                </a>
              </div>
            </div>
          </div>
        }
      </div>
    </section>);

};

export default LeadershipSection;